'use client';
import React, { useState, useMemo } from 'react';
import { X, Activity, Zap, GitBranch, ArrowRight, Trash2 } from 'lucide-react';
import type { CanvasNode, CanvasEdge } from '@/lib/archsim-data';
import { ALL_COMPONENTS } from '@/lib/archsim-data';
import type { SimulationState, SimMetrics } from './ArchitectureCanvasScreen';
import type { ChaosState, ChaosEventId, TraceEntry } from '@/lib/simulation-engine';
import { generateDynamicTraces } from '@/lib/simulation-engine';



interface PathMonitorDrawerProps {
  nodes: CanvasNode[];
  edges: CanvasEdge[];
  selectedEdgeId?: string | null;
  simState: SimulationState;
  metrics: SimMetrics;
  chaosState: ChaosState;
  onToggleChaos: (eventId: ChaosEventId) => void;
  onDeleteEdge?: (id: string) => void;
  onClose: () => void;
}

type PathTab = 'metrics' | 'chaos' | 'trace';

const CHAOS_EXPERIMENTS: { id: ChaosEventId; label: string; severity: 'critical' | 'warning'; desc: string }[] = [
  { id: 'chaos-kill-random', label: 'Kill Random Node', severity: 'critical', desc: 'Terminates a random active node — sets errorRate to 100%, RPS to 0' },
  { id: 'chaos-latency-spike', label: 'Inject 2s Latency', severity: 'warning', desc: 'Adds +2000ms to all node processing times for ~30 s' },
  { id: 'chaos-packet-loss', label: '30% Packet Loss', severity: 'warning', desc: 'Drops 30% of messages — +2.5% error rate, +43% latency' },
  { id: 'chaos-cpu-throttle', label: 'CPU Throttle 10%', severity: 'critical', desc: 'Limits compute nodes to 10% capacity — queues saturate' },
  { id: 'chaos-db-slow', label: 'DB Query Slowdown', severity: 'warning', desc: 'Reduces DB throughput to 10% — 10× query latency' },
  { id: 'chaos-network-partition', label: 'Network Partition', severity: 'critical', desc: 'Splits nodes into two isolated halves — partition A goes dark' },
  { id: 'chaos-memory-leak', label: 'Memory Leak', severity: 'warning', desc: 'CPU load ramps +1.5% per tick until OOM on all nodes' },
  { id: 'chaos-cascade', label: 'Cascade Failure', severity: 'critical', desc: 'Kills one node; surviving nodes get +50% load from rerouted traffic' },
];

export default function PathMonitorDrawer({
  nodes, edges, selectedEdgeId, simState, metrics, chaosState, onToggleChaos, onDeleteEdge, onClose,
}: PathMonitorDrawerProps) {
  const [activeTab, setActiveTab] = useState<PathTab>('metrics');
  const isActive = simState !== 'stopped';

  // Selected edge details
  const selectedEdge = selectedEdgeId ? edges.find(e => e.id === selectedEdgeId) ?? null : null;
  const edgeSrcNode = selectedEdge ? nodes.find(n => n.id === selectedEdge.sourceId) ?? null : null;
  const edgeTgtNode = selectedEdge ? nodes.find(n => n.id === selectedEdge.targetId) ?? null : null;
  const edgeSrcDef = edgeSrcNode ? ALL_COMPONENTS.find(c => c.id === edgeSrcNode.defId) : null;
  const edgeTgtDef = edgeTgtNode ? ALL_COMPONENTS.find(c => c.id === edgeTgtNode.defId) : null;
  const edgeRps = edgeSrcNode && edgeTgtNode
    ? Math.round((edgeSrcNode.metrics.rps + edgeTgtNode.metrics.rps) / 2)
    : 0;
  const edgeLatency = edgeSrcNode && edgeTgtNode
    ? Math.round((edgeSrcNode.metrics.latency + edgeTgtNode.metrics.latency) / 2)
    : 0;

  // Protocol color
  const protocolColor = selectedEdge
    ? selectedEdge.protocol === 'AMQP' ? '#e879f9'
      : selectedEdge.protocol === 'gRPC' ? '#38bdf8'
      : selectedEdge.protocol === 'WebSocket'? '#f59e0b' :'#6ee7b7' :'#6ee7b7';

  // Build actual topological path: find the longest chain through the DAG
  const pathNodes = React.useMemo(() => {
    if (nodes.length === 0) return [];
    const adj = new Map<string, string[]>();
    for (const n of nodes) adj.set(n.id, []);
    for (const e of edges) {
      if (adj.has(e.sourceId)) adj.get(e.sourceId)!.push(e.targetId);
    }
    const hasIncoming = new Set(edges.map(e => e.targetId));
    const sources = nodes.filter(n => !hasIncoming.has(n.id));
    const nodeMap = new Map(nodes.map(n => [n.id, n]));
    let longestPath: CanvasNode[] = [];

    function dfs(nodeId: string, path: CanvasNode[], visited: Set<string>) {
      if (visited.has(nodeId)) return;
      visited.add(nodeId);
      const node = nodeMap.get(nodeId);
      if (!node) return;
      const newPath = [...path, node];
      if (newPath.length > longestPath.length) longestPath = newPath;
      const children = adj.get(nodeId) ?? [];
      for (const child of children) {
        dfs(child, newPath, new Set(visited));
      }
    }

    const startNodes = sources.length > 0 ? sources : nodes.slice(0, 1);
    for (const src of startNodes) {
      dfs(src.id, [], new Set());
    }
    // Cap at 6 for display
    return longestPath.slice(0, 6);
  }, [nodes, edges]);

  const totalPathLatency = pathNodes.reduce((sum, n) => sum + n.metrics.latency, 0);

  // Dynamic traces from topology
  const traces: TraceEntry[] = isActive
    ? generateDynamicTraces(nodes, edges, metrics.tick)
    : [];

  const tabs = [
    { id: 'metrics' as PathTab, label: 'Metrics', icon: Activity },
    { id: 'chaos' as PathTab, label: 'Chaos', icon: Zap },
    { id: 'trace' as PathTab, label: 'Trace', icon: GitBranch },
  ];

  return (
    <div className="border-t border-border bg-card/95 backdrop-blur-sm animate-slide-up" style={{ height: '260px' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 sm:px-4 py-2 border-b border-border">
        <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto scrollbar-thin">
          <span className="text-sm font-semibold text-foreground shrink-0">Path Monitor</span>
          <div className="flex shrink-0">
            {tabs.map(tab => {
              const TabIcon = tab.icon;
              return (
                <button
                  key={`path-tab-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 text-xs font-medium transition-all ${
                    activeTab === tab.id ? 'tab-active' : 'tab-inactive'
                  }`}
                >
                  <TabIcon size={11} />
                  {tab.label}
                </button>
              );
            })}
          </div>
          {/* Active chaos indicator */}
          {chaosState.activeEvents.size > 0 && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-danger/10 border border-danger/30 text-danger text-xs font-medium shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-danger animate-pulse" />
              {chaosState.activeEvents.size} active
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0 ml-2">
          {selectedEdge && onDeleteEdge && (
            <button
              onClick={() => { onDeleteEdge(selectedEdge.id); onClose(); }}
              title="Delete selected path"
              className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-danger hover:bg-danger/10 border border-danger/30 hover:border-danger/60 transition-all"
            >
              <Trash2 size={11} />
              <span className="hidden sm:inline">Delete Path</span>
            </button>
          )}
          <button onClick={onClose} className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-all">
            <X size={13} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex h-full overflow-hidden">
        {activeTab === 'metrics' && (
          <div className="flex-1 flex flex-col sm:flex-row overflow-hidden">
            {/* Selected edge banner */}
            {selectedEdge && edgeSrcNode && edgeTgtNode && (
              <div className="flex items-center gap-2 px-3 sm:px-4 py-1.5 border-b border-border bg-secondary/50 shrink-0 overflow-x-auto scrollbar-thin">
                <span className="text-xs font-medium text-muted-foreground shrink-0">Inspecting path:</span>
                <span className="text-xs font-semibold text-foreground shrink-0">{edgeSrcDef?.icon} {edgeSrcNode.label}</span>
                <ArrowRight size={10} className="text-muted-foreground shrink-0" />
                <span className="text-xs font-semibold text-foreground shrink-0">{edgeTgtDef?.icon} {edgeTgtNode.label}</span>
                <span className="ml-1 px-1.5 py-0.5 rounded text-xs font-mono shrink-0" style={{ color: protocolColor, background: `${protocolColor}18`, border: `1px solid ${protocolColor}40` }}>
                  {selectedEdge.protocol}
                </span>
                {isActive && (
                  <>
                    <span className="text-xs text-muted-foreground shrink-0 ml-1">
                      <span className="font-mono-nums text-positive font-semibold">{edgeRps >= 1000 ? `${(edgeRps / 1000).toFixed(1)}k` : edgeRps}</span>
                      <span className="ml-0.5">rps</span>
                    </span>
                    <span className="text-xs text-muted-foreground shrink-0">
                      <span className={`font-mono-nums font-semibold ${edgeLatency > 500 ? 'text-danger' : edgeLatency > 100 ? 'text-warning' : 'text-positive'}`}>{edgeLatency}</span>
                      <span className="ml-0.5">ms avg</span>
                    </span>
                  </>
                )}
              </div>
            )}

            {/* Path visualization */}
            <div className="flex-1 flex items-center px-3 sm:px-6 gap-2 overflow-x-auto scrollbar-thin py-2">
              {!isActive ? (
                <p className="text-xs text-muted-foreground">Start simulation to see path metrics</p>
              ) : pathNodes.length === 0 ? (
                <p className="text-xs text-muted-foreground">Add nodes to the canvas to trace paths</p>
              ) : (
                pathNodes.map((node, i) => {
                  const def = ALL_COMPONENTS.find(c => c.id === node.defId);
                  const isBottleneck = node.metrics.cpuLoad > 80;
                  const isFailed = node.status === 'failed';
                  const isOnSelectedPath = selectedEdge
                    ? node.id === selectedEdge.sourceId || node.id === selectedEdge.targetId
                    : false;
                  return (
                    <React.Fragment key={`path-node-${node.id}`}>
                      <div className={`flex flex-col items-center shrink-0 p-2 rounded-lg border transition-all ${
                        isFailed ? 'border-danger/60 bg-danger/10' : isOnSelectedPath ?'border-violet-400/60 bg-violet-400/10': isBottleneck ?'border-warning/40 bg-warning/5' : 'border-border bg-secondary'
                      }`}>
                        <span className="text-base">{def?.icon ?? '⬡'}</span>
                        <span className="text-xs font-medium text-foreground mt-1 max-w-[70px] truncate">{node.label}</span>
                        <span className={`text-xs font-bold font-mono-nums mt-0.5 ${
                          isFailed ? 'text-danger' : isBottleneck ? 'text-warning' : 'text-positive'
                        }`}>
                          {isFailed ? 'FAILED' : `${node.metrics.latency}ms`}
                        </span>
                        <span className="text-xs text-muted-foreground font-mono-nums">{node.metrics.cpuLoad}% cpu</span>
                      </div>
                      {i < pathNodes.length - 1 && (
                        <div className="flex flex-col items-center shrink-0">
                          <div className="w-6 sm:w-8 h-px bg-primary/40" />
                          <span className="text-muted-foreground/60 mt-0.5" style={{ fontSize: '9px' }}>HTTP</span>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </div>

            {/* Summary */}
            {isActive && pathNodes.length > 0 && (
              <div className="hidden sm:flex w-40 border-l border-border p-3 flex-col gap-2 shrink-0">
                <div>
                  <p className="text-xs text-muted-foreground">Total Latency</p>
                  <p className="text-lg font-bold text-foreground font-mono-nums">{totalPathLatency.toFixed(1)}<span className="text-xs text-muted-foreground ml-1">ms</span></p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Hops</p>
                  <p className="text-lg font-bold text-foreground font-mono-nums">{pathNodes.length}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">System RPS</p>
                  <p className="text-lg font-bold text-positive font-mono-nums">
                    {metrics.rps >= 1000 ? `${(metrics.rps / 1000).toFixed(1)}k` : metrics.rps}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'chaos' && (
          <div className="flex-1 overflow-y-auto scrollbar-thin px-3 sm:px-4 py-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {CHAOS_EXPERIMENTS.map(exp => {
                const isOn = chaosState.activeEvents.has(exp.id);
                const startTick = chaosState.eventStartTick.get(exp.id);
                const elapsed = startTick !== undefined ? metrics.tick - startTick : 0;
                return (
                  <button
                    key={exp.id}
                    disabled={!isActive}
                    onClick={() => onToggleChaos(exp.id)}
                    className={`p-2.5 sm:p-3 rounded-lg border text-left transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                      isOn
                        ? exp.severity === 'critical' ? 'border-danger bg-danger/10 text-danger' : 'border-warning bg-warning/10 text-warning' :'border-border bg-secondary hover:border-primary/30 hover:bg-secondary/80'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <p className={`text-xs font-semibold ${
                        isOn ? exp.severity === 'critical' ? 'text-danger' : 'text-warning' : 'text-foreground'
                      }`}>{exp.label}</p>
                      {isOn && (
                        <span className={`text-xs font-mono-nums ${exp.severity === 'critical' ? 'text-danger' : 'text-warning'}`}>
                          {elapsed}t
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground leading-tight">{exp.desc}</p>
                    {isOn && (
                      <div className="mt-1.5 flex items-center gap-1">
                        <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${exp.severity === 'critical' ? 'bg-danger' : 'bg-warning'}`} />
                        <span className={`text-xs font-medium ${exp.severity === 'critical' ? 'text-danger' : 'text-warning'}`}>ACTIVE</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {!isActive && (
              <p className="text-xs text-muted-foreground/60 text-center mt-4">Start simulation to inject chaos</p>
            )}
          </div>
        )}

        {activeTab === 'trace' && (
          <div className="flex-1 overflow-y-auto scrollbar-thin px-3 sm:px-4 py-3">
            {!isActive ? (
              <p className="text-xs text-muted-foreground">Start simulation to see distributed traces</p>
            ) : traces.length === 0 ? (
              <p className="text-xs text-muted-foreground">Add connected nodes to generate traces</p>
            ) : (
              <div className="space-y-2">
                {traces.map((trace, idx) => (
                  <div
                    key={`trace-${trace.traceId}-${idx}`}
                    className="rounded-lg bg-secondary border border-border hover:border-primary/20 transition-all cursor-pointer overflow-hidden"
                  >
                    {/* Trace header */}
                    <div className="flex items-center gap-2 sm:gap-3 p-2.5">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${
                        trace.status === 'error' ? 'bg-danger' :
                        trace.status === 'slow' ? 'bg-warning' : 'bg-positive'
                      }`} />
                      <span className="text-xs font-mono text-muted-foreground shrink-0 hidden sm:inline">{trace.traceId}</span>
                      <span className="text-xs text-foreground flex-1 truncate">{trace.path}</span>
                      <span className="text-xs font-mono-nums text-muted-foreground shrink-0 hidden sm:inline">{trace.spans} spans</span>
                      <span className={`text-xs font-bold font-mono-nums shrink-0 ${
                        trace.duration > 500 ? 'text-danger' :
                        trace.duration > 100 ? 'text-warning' : 'text-positive'
                      }`}>{trace.duration}ms</span>
                    </div>
                    {/* Span waterfall (mini) */}
                    {trace.spanDetails.length > 1 && (
                      <div className="px-2.5 pb-2 flex gap-1">
                        {trace.spanDetails.map((span, si) => {
                          const pct = trace.duration > 0 ? (span.duration / trace.duration) * 100 : 0;
                          return (
                            <div
                              key={`span-${si}`}
                              title={`${span.service}: ${span.duration}ms`}
                              className={`h-1.5 rounded-sm transition-all ${
                                span.status === 'error' ? 'bg-danger' :
                                span.status === 'slow' ? 'bg-warning' : 'bg-positive/60'
                              }`}
                              style={{ width: `${Math.max(4, pct)}%` }}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}